import { Field, type FieldRootProps } from "@ark-ui/react";
import clsx from "clsx";
import * as React from "react";
import getFormHelperText, {
  type HelperTextProps,
} from "../../helper/HelperTextIcon.js";
import { CharacterCount } from "../../shared/character-count/CharacterCount.js";
import {
  FormLabel,
  type FormLabelClassNames,
  type LabelOptionProps,
} from "../../shared/form-label/FormLabel.js";
import { BaseInput, type BaseInputProps } from "../baseinput/BaseInput.js";

interface TextFieldProps extends BaseInputProps {
  label?: LabelOptionProps;
  helperText?: HelperTextProps;
  placeholder?: string;
  name?: string;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  asterisk?: boolean;
  computedValue?: string;
  fieldProps?: FieldRootProps;
  classNames?: {
    root?: string;
    label?: string;
    input?: string;
    inputWrapper?: string;
    helperText?: string;
    leftSection?: string;
    rightSection?: string;
    characterCount?: string;
  };
  labelClassNames?: FormLabelClassNames;
  characterCount?: string;
}

const TextField = React.forwardRef<React.ElementRef<"input">, TextFieldProps>(
  (props, ref) => {
    const {
      className,
      classNames,
      label,
      variant = "outline",
      size = "md",
      readOnly,
      disabled,
      computedValue,
      error,
      helperText = { message: "", status: "default" },
      leftSection,
      rightSection,
      name,
      asterisk = false,
      fieldProps,
      characterCount,
      labelClassNames,
      ...inputProps
    } = props;

    return (
      <Field.Root
        className={clsx("ox-reset ox-TextField", className, classNames?.root)}
        data-variant={variant}
        data-size={size}
        data-invalid={error ? true : false}
        data-left-section={leftSection ? true : false}
        data-right-section={rightSection ? true : false}
        disabled={disabled}
        {...fieldProps}
      >
        <FormLabel
          label={label}
          size={size}
          error={error}
          className={classNames?.label}
          classNames={labelClassNames}
        />

        <div
          className={clsx(
            "ox-reset ox-TextFieldInputWrapper",
            classNames?.inputWrapper,
          )}
        >
          {leftSection ? (
            <div
              className={clsx(
                "ox-TextFieldLeftSection ox-FormIcon",
                classNames?.leftSection,
              )}
              data-size={size}
            >
              {leftSection}
            </div>
          ) : null}
          <BaseInput
            ref={ref}
            className={clsx(classNames?.input)}
            size={size}
            data-invalid={error ? true : false}
            name={name}
            readOnly={readOnly}
            disabled={disabled}
            aria-readonly={readOnly}
            variant={variant}
            data-left-section={leftSection ? true : false}
            data-right-section={rightSection ? true : false}
            {...inputProps}
          />
          {computedValue ? (
            <div
              className="ox-reset ox-TextFieldComputedValue"
              data-size={size}
            >
              <span>{computedValue}</span>
            </div>
          ) : null}
          {rightSection ? (
            <div
              className={clsx(
                "ox-TextFieldRightSection ox-FormIcon",
                classNames?.rightSection,
              )}
              data-size={size}
            >
              {rightSection}
            </div>
          ) : null}
        </div>
        {helperText?.message ? (
          <Field.HelperText
            data-size={size}
            data-status={helperText?.status}
            className={clsx(
              "ox-reset ox-FormHelperText",
              classNames?.helperText,
            )}
            data-invalid={
              helperText?.status === "error" || error ? true : false
            }
          >
            {getFormHelperText(helperText, size)}
          </Field.HelperText>
        ) : null}
        {characterCount ? (
          <CharacterCount size={size} className={classNames?.characterCount}>
            {characterCount}
          </CharacterCount>
        ) : null}
      </Field.Root>
    );
  },
);
TextField.displayName = "TextField";
export { TextField };
export type { TextFieldProps };
